// Robot Module - Components
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  SmartToy as RobotIcon,
  Battery20 as BatteryIcon,
  LocationOn as LocationIcon,
  Route as TourIcon,
  AccessTime as TimeIcon,
  Videocam as VideoIcon,
  PlayArrow as StartTourIcon,
} from '@mui/icons-material';
import { Robot } from '../types';
import { Tour } from '../../tour/types';

interface RobotCardProps {
  robot: Robot;
  canInitiateCalls: boolean;
  onCallRobot: (robot: Robot) => void;
  availableTours?: Tour[]; // Tours from dashboard to install
  installedTours?: any[]; // Tours already installed on robot
  activatedTours?: any[]; // Tours currently activated on robot
  onInstallTour?: (robot: Robot, tourId: number) => void;
  onActivateTour?: (robot: Robot, tourId: string) => void;
  onRefreshInstalledTours?: (robot: Robot) => void;
}

export const RobotCard: React.FC<RobotCardProps> = ({
  robot,
  canInitiateCalls,
  onCallRobot,
  availableTours = [],
  installedTours = [],
  activatedTours = [],
  onInstallTour,
  onActivateTour,
  onRefreshInstalledTours,
}) => {
  const [selectedTourToInstall, setSelectedTourToInstall] = useState<string>('');
  const [selectedInstalledTour, setSelectedInstalledTour] = useState<string>('');

  // Debug tours
  console.log('🔍 RobotCard - Available tours (for installation):', availableTours);
  console.log('🔍 RobotCard - Available tours length:', availableTours?.length);
  console.log('🔍 RobotCard - Available tours is array?', Array.isArray(availableTours));
  if (availableTours && availableTours.length > 0) {
    const firstTour = availableTours[0] as any;
    console.log('🔍 RobotCard - First available tour details:', {
      id: firstTour?.id,
      tour_id: firstTour?.tour_id,
      description: firstTour?.description,
      name: firstTour?.name,
      fullObject: firstTour,
    });
  }
  console.log('🔍 RobotCard - Installed tours (for activation):', installedTours);
  console.log('🔍 RobotCard - Installed tours length:', installedTours?.length);
  console.log('🔍 RobotCard - Installed tours is array?', Array.isArray(installedTours));
  if (installedTours && installedTours.length > 0) {
    const firstInstalledTour = installedTours[0] as any;
    console.log('🔍 RobotCard - First installed tour details:', {
      tour_id: firstInstalledTour?.tour_id,
      id: firstInstalledTour?.id,
      yaml_url: firstInstalledTour?.yaml_url,
      name: firstInstalledTour?.name,
      tour_name: firstInstalledTour?.tour_name,
      fullObject: firstInstalledTour,
    });
  }
  const getStatusColor = (status: string, isOnline: boolean) => {
    if (!isOnline) return 'error';

    switch (status.toLowerCase()) {
      case 'idle':
        return 'success';
      case 'busy':
      case 'executing':
        return 'warning';
      case 'charging':
        return 'info';
      case 'initializing':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getBatteryColor = (level: number | null) => {
    if (level === null) return 'default';
    if (level > 50) return 'success';
    if (level > 20) return 'warning';
    return 'error';
  };

  const formatLocation = (location: Robot['location']) => {
    if (location.zone) return location.zone;
    if (location.floor) return `Floor ${location.floor}`;
    return `(${location.x.toFixed(1)}, ${location.y.toFixed(1)})`;
  };

  const formatLastSeen = (lastSeen: string) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const handleInstallTour = () => {
    if (selectedTourToInstall && onInstallTour) {
      onInstallTour(robot, parseInt(selectedTourToInstall));
      setSelectedTourToInstall('');
    }
  };

  const handleActivateTour = () => {
    if (selectedInstalledTour && onActivateTour) {
      onActivateTour(robot, selectedInstalledTour);
      setSelectedInstalledTour('');
    }
  };

  const handleRefreshInstalledTours = () => {
    if (onRefreshInstalledTours) {
      onRefreshInstalledTours(robot);
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Robot Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: robot.is_online ? 'success.main' : 'grey.500', mr: 2 }}>
            <RobotIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h3">
              {robot.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {robot.robot_id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip
              label={robot.is_online ? 'ONLINE' : 'OFFLINE'}
              color={robot.is_online ? 'success' : 'error'}
              size="small"
            />
            <Chip
              label={robot.status.toUpperCase()}
              color={getStatusColor(robot.status, robot.is_online) as any}
              size="small"
            />
          </Box>
        </Box>

        {/* Robot Details */}
        <Box sx={{ mb: 2 }}>
          {/* Battery Information */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BatteryIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2">
              Battery: {robot.battery_level !== null ? `${robot.battery_level}%` : 'Unknown'}
            </Typography>
            {robot.battery_status && (
              <Chip
                label={robot.battery_status}
                color={getBatteryColor(robot.battery_level) as any}
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          {/* Location Information */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2">Location: {formatLocation(robot.location)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (Heading: {Math.round((robot.location.heading || 0) * (180 / Math.PI))}°)
            </Typography>
          </Box>

          {/* Tour Information */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TourIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2">
              Tour:{' '}
              {activatedTours && activatedTours.length > 0
                ? activatedTours.map((tour: any) => tour.tour_name || `Tour ${tour.tour_id}`).join(', ')
                : robot.current_tour || 'No active tour'}
            </Typography>
            {activatedTours && activatedTours.length > 0 && (
              <Chip label="ACTIVE" color="success" size="small" sx={{ ml: 1 }} />
            )}
          </Box>

          {/* Health Status */}
          {robot.health_status && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Health: {robot.health_status}
              </Typography>
            </Box>
          )}

          {/* State Information */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              State: {robot.state}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimeIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2">Last seen: {formatLastSeen(robot.last_seen)}</Typography>
          </Box>
        </Box>

        {/* Tour Installation Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Install Tour to Robot
          </Typography>
          {availableTours && Array.isArray(availableTours) && availableTours.length > 0 ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tour to Install</InputLabel>
                <Select
                  value={selectedTourToInstall}
                  label="Select Tour to Install"
                  onChange={e => setSelectedTourToInstall(e.target.value)}
                  disabled={!robot.is_online}
                >
                  <MenuItem value="">
                    <em>No tour selected</em>
                  </MenuItem>
                  {availableTours
                    .filter(tour => {
                      const tourAny = tour as any;
                      const tourId = tourAny?.id;
                      if (!tourId) {
                        console.warn(`⚠️ Filtering out tour missing id:`, tourAny);
                        return false;
                      }
                      return true;
                    })
                    .map((tour, index) => {
                      // Handle dashboard Tour type (from useTours) - Tour has id, tour_id, and description
                      const tourAny = tour as any;
                      // Dashboard tours have both id (primary key) and tour_id (museum-specific tour ID)
                      const tourId = tourAny.id; // Use id for selection (primary key)
                      // Tour interface uses 'description' for the name, not 'name'
                      // If description is empty, use tour_id or id as fallback
                      const tourName =
                        (tourAny.description && tourAny.description.trim()) ||
                        tourAny.name ||
                        tourAny.tour_name ||
                        `Tour ${tourAny.tour_id || tourAny.id || index}`;
                      const tourValue = tourAny.id; // Use id (primary key) for the value

                      console.log(`🔍 Rendering tour option:`, { tourId, tourName, tourValue, tour: tourAny });

                      return (
                        <MenuItem key={tourValue || index} value={tourValue?.toString() || index.toString()}>
                          {tourName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>

              {selectedTourToInstall && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TourIcon />}
                  onClick={handleInstallTour}
                  disabled={!robot.is_online}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Install Tour
                </Button>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No tours available from dashboard
            </Typography>
          )}
        </Box>

        {/* Installed Tours Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Available Tours on Robot</Typography>
            <Button size="small" onClick={handleRefreshInstalledTours} disabled={!robot.is_online}>
              Refresh
            </Button>
          </Box>

          {false && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tour to Activate</InputLabel>
                <Select
                  value={selectedInstalledTour}
                  label="Select Tour to Activate"
                  onChange={e => setSelectedInstalledTour(e.target.value)}
                  disabled={!robot.is_online || (installedTours && installedTours.length === 0)}
                >
                  <MenuItem value="">
                    <em>{installedTours && installedTours.length > 0 ? 'No tour selected' : 'No tours available'}</em>
                  </MenuItem>
                  {installedTours && installedTours.length > 0
                    ? installedTours.map((tour, index) => {
                        // Handle different tour formats from API gateway
                        const tourAny = tour as any;
                        // API gateway tours may have tour_id, id, or other identifiers
                        const tourId = tourAny.tour_id || tourAny.id || tourAny.tourId || index;
                        const tourName = tourAny.name || tourAny.tour_name || tourAny.description || `Tour ${tourId}`;
                        // For activation, we need the yaml_url or tour_id
                        const tourValue =
                          tourAny.yaml_url || tourAny.tour_id || tourAny.id || tourAny.tourId || index.toString();
                        const tourKey = tourAny.tour_id || tourAny.id || tourAny.tourId || tourAny.yaml_url || index;

                        console.log(`🔍 Rendering installed tour option:`, {
                          tourId,
                          tourName,
                          tourValue,
                          tour: tourAny,
                        });

                        return (
                          <MenuItem key={tourKey || index} value={tourValue.toString()}>
                            {tourName}
                          </MenuItem>
                        );
                      })
                    : null}
                </Select>
              </FormControl>

              {selectedInstalledTour && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<StartTourIcon />}
                  onClick={handleActivateTour}
                  disabled={!robot.is_online}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Activate Tour
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Call Button */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<VideoIcon />}
            onClick={() => onCallRobot(robot)}
            disabled={!canInitiateCalls || robot.status === 'offline'}
            fullWidth
            color="primary"
          >
            {robot.status === 'offline' ? 'Robot Offline' : 'Call Robot'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
